import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Button,
  Paragraph,
  LoadingContainer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  FEATURE_GATES,
  GlobalFeatureGateFilter,
  hasActiveFeatureGate,
  PermissionFilter,
  useAuth
} from '@sopranodesign/pulse-auth';
import { usePaginationReducer } from '@sopranodesign/pulse-components';

import {
  getZendeskTicketFilters
} from '../api';
import ZendeskTicketFiltersList from './ZendeskTicketFiltersList';
import ZendeskTicketsList from './ZendeskTicketsList';
import ZendeskTicketModalManager from './ZendeskTicketModalManager';

const ZendeskTickets = ({ settings }) => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const ticketsPagination = usePaginationReducer({
    pathname: '/zendesk-tickets',
    fetchQueryKey: ['generative-ai/zendesk/tickets']
  });

  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});

  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [isViewingTickets, setIsViewingTickets] = useState(false);

  const [filterLoadingIds, setFilterLoadingIds] = useState({});
  const [ticketLoadingIds, setTicketLoadingIds] = useState({});
  const [ticketErrorIds, setTicketErrorIds] = useState({});
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  const {
    data,
    isLoading
  } = useQuery({
    queryKey: [
      'generative-ai/zendesk/tickets',
      ticketsPagination.params
    ],
    queryFn: () =>
      getZendeskTicketFilters(
        ticketsPagination.params
      ),
    enabled:
      hasActiveFeatureGate(
        FEATURE_GATES.GENERATIVE_AI_ZENDESK_TICKETS
      )(auth),
    keepPreviousData: true,
    refetchInterval: shouldRefetch
      ? 5000
      : false
  });

  const response = useMemo(
    () => data?.data ?? {},
    [data]
  );
  const filters = useMemo(
    () => response.filters ?? [],
    [response]
  );
  const meta = response.meta ?? {};
  const hasPending =
    response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(
      !isViewingTickets &&
      hasPending
    );
  }, [
    hasPending,
    isViewingTickets,
    filters
  ]);

  useEffect(() => {
    if (!isViewingTickets) {
      setTicketLoadingIds({});
    }
  }, [isViewingTickets]);

  if (isLoading) {
    return (
      <Flex col>
        <Flex col center middle tall>
          <LoadingContainer />
          <Paragraph mt>Loading Tickets...</Paragraph>
        </Flex>
      </Flex>
    );
  }

  return (
    <GlobalFeatureGateFilter
      for={
        FEATURE_GATES.GENERATIVE_AI_ZENDESK_TICKETS
      }
    >
      {filters.length === 0 && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>
              Sync Zendesk tickets filtered by
              tag or organization.
            </Paragraph>
            <PermissionFilter can="edit generative ai resource">
              <PermissionFilter can="edit generative ai zendesk tickets">
                <Button
                  icon="plus"
                  variant="secondary"
                  onClick={() =>
                    openModal('addFilter')
                  }
                >
                  Add Ticket Filter
                </Button>
              </PermissionFilter>
            </PermissionFilter>
          </Flex>
        </Box>
      )}

      {filters.length > 0 && (
        <ZendeskTicketFiltersList
          filters={filters}
          settings={settings}
          loadingIds={filterLoadingIds}
          isSyncing={isSyncingAll}
          hasPending={hasPending}
          pagination={{
            meta,
            props:
              ticketsPagination.props
          }}
          onView={(id) => {
            setSelectedFilterId(id);
            setIsViewingTickets(true);
          }}
          onResync={(filter) =>
            openModal('confirmation', {
              type: 'resyncFilter',
              filter
            })
          }
          onDelete={(filter) =>
            openModal('confirmation', {
              type: 'deleteFilter',
              filter
            })
          }
          onAddFilter={() =>
            openModal('addFilter')
          }
          onResyncAll={() =>
            openModal('confirmation', {
              type: 'resyncAllFilters'
            })
          }
        />
      )}

      {selectedFilterId && (
        <ZendeskTicketsList
          filterId={selectedFilterId}
          handleHide={() => {
            setSelectedFilterId(null);
            setIsViewingTickets(false);

            queryClient.invalidateQueries({
              queryKey: [
                'generative-ai/zendesk/tickets'
              ]
            });
          }}
          onOpenTagsModal={(ticket) =>
            openModal('tags', {
              ticket,
              filterId:
                selectedFilterId
            })
          }
          onOpenRecrawlModal={(
            ticket
          ) =>
            openModal('confirmation', {
              type: 'recrawlTicket',
              ticket,
              filterId:
                selectedFilterId
            })
          }
          loadingIds={ticketLoadingIds}
          errorIds={ticketErrorIds}
        />
      )}

      <ZendeskTicketModalManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        setErrorIds={setTicketErrorIds}
        setFilterLoadingIds={
          setFilterLoadingIds
        }
        setTicketLoadingIds={
          setTicketLoadingIds
        }
        setIsSyncingAll={setIsSyncingAll}
      />
    </GlobalFeatureGateFilter>
  );
};

ZendeskTickets.propTypes = {
  settings: PropTypes.shape({
    status: PropTypes.number,
    uses_oauth: PropTypes.bool,
    updated_at: PropTypes.string
  })
};

export default ZendeskTickets;